import { computed, ref } from "vue";
import { useCustomersStore } from "@/customers/stores/customers";
import { debounce } from "@/shared/utils/debounce";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import type { Customer } from "@/customers/types/customer";

interface UseCustomerSearchOptions {
  pageSize?: number;
  debounceMs?: number;
  errorMessage?: string;
  onError?: (message: string) => void;
}

export const useCustomerSearch = (options: UseCustomerSearchOptions = {}) => {
  const customersStore = useCustomersStore();
  const searchTerm = ref("");
  const results = ref<Customer[]>([]);
  const hasMore = ref(false);
  const nextPageToken = ref<string>();
  const currentPageIndex = ref(0);
  const pageTokens = ref<Array<string | undefined>>([undefined]);
  const hasSearched = ref(false);
  const isLoading = ref(false);
  const errorMessage = ref("");
  const requestCounter = ref(0);

  const pageSize = options.pageSize ?? 10;
  const debounceMs = options.debounceMs ?? 500;

  const isActive = computed(() => searchTerm.value.trim().length > 0);
  const canGoPrevious = computed(() => currentPageIndex.value > 0);
  const canGoNext = computed(
    () => hasMore.value && !!nextPageToken.value && results.value.length > 0,
  );
  const currentPage = computed(() => currentPageIndex.value + 1);

  const resetPagination = () => {
    currentPageIndex.value = 0;
    pageTokens.value = [undefined];
    nextPageToken.value = undefined;
    hasMore.value = false;
  };

  const invalidateRequests = () => {
    requestCounter.value += 1;
  };

  const clearSearch = () => {
    debouncedSearch.cancel();
    invalidateRequests();
    searchTerm.value = "";
    resetPagination();
    results.value = [];
    errorMessage.value = "";
    hasSearched.value = false;
    isLoading.value = false;
  };

  const runSearch = async (pageToken?: string, pageIndex = 0) => {
    const currentSearchTerm = searchTerm.value.trim();
    if (!currentSearchTerm) {
      clearSearch();
      return;
    }

    const requestId = requestCounter.value + 1;
    requestCounter.value = requestId;
    isLoading.value = true;
    errorMessage.value = "";

    try {
      const response = await customersStore.searchCustomersPage({
        searchTerm: currentSearchTerm,
        limit: pageSize,
        ...(pageToken ? { page: pageToken } : {}),
      });

      if (requestId !== requestCounter.value || currentSearchTerm !== searchTerm.value.trim()) {
        return;
      }

      results.value = Array.isArray(response?.data) ? response.data : [];
      hasMore.value = !!response?.has_more;
      nextPageToken.value = response?.next_page;
      currentPageIndex.value = pageIndex;
      hasSearched.value = true;
    } catch (error) {
      if (requestId !== requestCounter.value || currentSearchTerm !== searchTerm.value.trim()) {
        return;
      }

      const resolvedErrorMessage = getErrorMessage(error, options.errorMessage);
      results.value = [];
      hasMore.value = false;
      nextPageToken.value = undefined;
      hasSearched.value = true;
      errorMessage.value = resolvedErrorMessage;
      options.onError?.(resolvedErrorMessage);
    } finally {
      if (requestId === requestCounter.value) {
        isLoading.value = false;
      }
    }
  };

  const debouncedSearch = debounce(async (term: string) => {
    if (term !== searchTerm.value.trim()) {
      return;
    }

    resetPagination();
    await runSearch(undefined, 0);
  }, debounceMs);

  const handleSearchInput = (value: string | null) => {
    searchTerm.value = value ?? "";
    const trimmedValue = searchTerm.value.trim();

    if (!trimmedValue) {
      clearSearch();
      return;
    }

    debouncedSearch.cancel();
    invalidateRequests();
    resetPagination();
    results.value = [];
    isLoading.value = true;
    hasSearched.value = false;
    errorMessage.value = "";
    debouncedSearch(trimmedValue);
  };

  const nextPage = async () => {
    if (!canGoNext.value || !nextPageToken.value) {
      return;
    }

    const nextIndex = currentPageIndex.value + 1;
    pageTokens.value[nextIndex] = nextPageToken.value;
    await runSearch(nextPageToken.value, nextIndex);
  };

  const previousPage = async () => {
    if (!canGoPrevious.value) {
      return;
    }

    const previousIndex = currentPageIndex.value - 1;
    await runSearch(pageTokens.value[previousIndex], previousIndex);
  };

  return {
    searchTerm,
    results,
    isLoading,
    errorMessage,
    hasSearched,
    hasMore,
    isActive,
    canGoPrevious,
    canGoNext,
    currentPage,
    handleSearchInput,
    runSearch,
    nextPage,
    previousPage,
    clearSearch,
  };
};
